// src/hooks/useFirestore.js

import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebaseConfig';
import {
    collection,
    query,
    onSnapshot,
    orderBy,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    documentId,
    getDocs
} from 'firebase/firestore';

/**
 * Hook para buscar, adicionar, atualizar e deletar documentos de uma coleção primária em tempo real.
 * @param {string} collectionName O nome da coleção (ex: 'parameters/product-categories').
 * @param {string} [orderField] O campo pelo qual os documentos devem ser ordenados.
 */
const useFirestore = (collectionName, orderField = null) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Efeito para a busca em tempo real (Read)
    useEffect(() => {
        // 🛑 CORREÇÃO: Ignorar a execução se o nome da coleção for nulo ou vazio.
        // Isso evita o erro "Function collection() cannot be called with an empty path."
        if (!collectionName) {
            setData([]);
            setLoading(false);
            setError(null);
            return; // Sai do useEffect sem chamar collection()
        }

        let q = collection(db, collectionName);

        if (orderField) {
            // Adiciona a ordenação se o campo for fornecido
            q = query(q, orderBy(orderField));
        } else {
            // Se não houver ordenação, buscamos pelo ID para garantir consistência
            q = query(q, orderBy(documentId()));
        }

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const items = [];
            querySnapshot.forEach((document) => {
                items.push({ id: document.id, ...document.data() });
            });
            setData(items);
            setLoading(false);
            setError(null);
        },
            (err) => {
                console.error(`Erro ao buscar dados da coleção ${collectionName}:`, err);
                setError("Falha ao carregar os dados.");
                setLoading(false);
            });

        return () => unsubscribe();
    }, [collectionName, orderField]);

    // NOVA FUNÇÃO: Força uma atualização manual dos dados
    const refreshData = useCallback(async () => {
        if (!collectionName) {
            console.error("Erro: Tentativa de refresh com collectionName vazio.");
            return { success: false, error: "Nome da coleção não fornecido." };
        }

        try {
            setLoading(true);
            let q = collection(db, collectionName);

            if (orderField) {
                q = query(q, orderBy(orderField));
            } else {
                q = query(q, orderBy(documentId()));
            }

            const querySnapshot = await getDocs(q);
            const items = [];
            querySnapshot.forEach((document) => {
                items.push({ id: document.id, ...document.data() });
            });
            setData(items);
            setError(null);
            return { success: true };
        } catch (err) {
            console.error(`Erro ao atualizar dados da coleção ${collectionName}:`, err);
            setError("Falha ao atualizar os dados.");
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, [collectionName, orderField]);

    // Funções de CRUD (Create, Update, Delete)

    // 1. CREATE (Adicionar Documento)
    const addDocument = useCallback(async (documentData) => {
        try {
            // 🛑 Verificação: Bloqueia a execução se collectionName for null/vazio.
            if (!collectionName) {
                console.error("Erro: Tentativa de adicionar documento com collectionName vazio.");
                return { success: false, error: "Nome da coleção não fornecido." };
            }
            await addDoc(collection(db, collectionName), documentData);
            return { success: true };
        } catch (err) {
            console.error("Erro ao adicionar documento:", err);
            return { success: false, error: err.message };
        }
    }, [collectionName]);

    // 2. UPDATE (Atualizar Documento)
    const updateDocument = useCallback(async (id, documentData) => {
        try {
            // 🛑 Verificação: Bloqueia a execução se collectionName for null/vazio.
            if (!collectionName) {
                console.error("Erro: Tentativa de atualizar documento com collectionName vazio.");
                return { success: false, error: "Nome da coleção não fornecido." };
            }
            const docRef = doc(db, collectionName, id);
            await updateDoc(docRef, documentData);
            return { success: true };
        } catch (err) {
            console.error(`Erro ao atualizar documento com ID ${id}:`, err);
            return { success: false, error: err.message };
        }
    }, [collectionName]);

    // 3. DELETE (Deletar Documento)
    const deleteDocument = useCallback(async (id) => {
        try {
            // 🛑 Verificação: Bloqueia a execução se collectionName for null/vazio.
            if (!collectionName) {
                console.error("Erro: Tentativa de deletar documento com collectionName vazio.");
                return { success: false, error: "Nome da coleção não fornecido." };
            }
            const docRef = doc(db, collectionName, id);
            await deleteDoc(docRef);
            return { success: true };
        } catch (err) {
            console.error(`Erro ao deletar documento com ID ${id}:`, err);
            return { success: false, error: err.message };
        }
    }, [collectionName]);


    return {
        data,
        loading,
        error,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData  // Adicionado ao retorno do hook
    };
};

export default useFirestore;